<script>
import { GlForm, GlButton } from '@gitlab/ui';
import { ValidationObserver } from 'vee-validate';
import csrf from '~/javascripts/lib/csrf';
import InputField from '~/javascripts/forms/components/input_field.vue';
import NameField from '~/javascripts/forms/components/name_field.vue';
import CompanySizeField from '~/javascripts/forms/components/company_size_field.vue';
import CountrySelect from '~/javascripts/forms/components/country_select.vue';
import StateSelect from '~/javascripts/forms/components/state_select.vue';
import { FIELD_VALIDATIONS } from '~/javascripts/forms/validation/constants';
import TaxIdHelpIconPopover from '~/javascripts/forms/components/tax_id_help_icon_popover.vue';

export default {
  name: 'CustomerInfo',
  components: {
    TaxIdHelpIconPopover,
    GlForm,
    GlButton,
    InputField,
    NameField,
    CompanySizeField,
    CountrySelect,
    StateSelect,
    ValidationObserver,
  },
  inject: {
    countriesToHideVatId: {
      default: [],
    },
  },
  props: {
    registerPath: {
      type: String,
      required: true,
    },
    accountInfo: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      // Form validation requires inputs to be v-model'd
      form: {
        firstName: null,
        lastName: null,
        address1: null,
        address2: null,
        city: null,
        zipCode: null,
        companyName: this.accountInfo?.organization,
        companySize: '',
        taxId: null,
      },
      selectedCountry: '',
      csrfToken: csrf.token,
    };
  },
  computed: {
    taxFieldVisible() {
      if (!this.selectedCountry) {
        return false;
      }
      return !this.countriesToHideVatId.includes(this.selectedCountry);
    },
    customerName() {
      const fullName = this.accountInfo?.name;
      if (!fullName) return undefined;

      const [firstName, ...restOfName] = fullName.trim().split(' ');
      const [lastName] = restOfName.slice(-1);

      return {
        firstName,
        lastName,
      };
    },
  },
  created() {
    this.initializeForm();
  },
  methods: {
    initializeForm() {
      this.form.firstName = this.customerName?.firstName;
      this.form.lastName = this.customerName?.lastName;
    },
    countryChanged(country) {
      this.selectedCountry = country;
    },
    onSubmit(event, handleSubmit) {
      handleSubmit(() => event.target.submit());
    },
  },
  FIELD_VALIDATIONS,
};
</script>

<template>
  <validation-observer #default="{ handleSubmit }" slim>
    <gl-form method="post" :action="registerPath" @submit.prevent="onSubmit($event, handleSubmit)">
      <input name="utf8" type="hidden" value="✓" />
      <input type="hidden" name="authenticity_token" :value="csrfToken" />

      <div class="gl-grid gl-grid-cols-2 gl-gap-x-6 gl-gap-y-5 gl-mb-5">
        <h3 class="gl-mb-0 gl-col-span-2 gl-leading-24 gl-text-gray-500">Personal information</h3>

        <name-field
          id="first-name"
          v-model.trim="form.firstName"
          label="First name"
          test-id="first-name"
          name="customer[first_name]"
          group-class="gl-mb-0"
        />
        <name-field
          id="last-name"
          v-model.trim="form.lastName"
          label="Last name"
          test-id="last-name"
          name="customer[last_name]"
          group-class="gl-mb-0"
        />

        <h3 class="gl-mb-0 gl-mt-2 gl-col-span-2 gl-leading-24 gl-text-gray-500">
          Company information
        </h3>

        <input-field
          id="company-name"
          v-model="form.companyName"
          test-id="company-name"
          group-class="gl-mb-0 gl-col-span-2"
          label="Company / organization name"
          name="customer[company]"
          rules="required|noEmoji"
        />

        <company-size-field v-model="form.companySize" group-class="gl-mb-0 gl-col-span-2" />

        <input-field
          id="address-one"
          v-model="form.address1"
          label="Company / organization address"
          :optional="true"
          test-id="address-one"
          name="customer[address_1]"
          rules="noEmoji"
          group-class="gl-mb-0 gl-col-span-2"
        />
        <input-field
          id="address-two"
          v-model="form.address2"
          label="Company / organization address 2"
          :hide-label="true"
          :optional="true"
          optional-text=""
          test-id="address-two"
          name="customer[address_2]"
          rules="noEmoji"
          group-class="gl-mb-0 gl-col-span-2"
        />

        <input-field
          id="city"
          v-model="form.city"
          label="City"
          test-id="city"
          name="customer[city]"
          :rules="$options.FIELD_VALIDATIONS.city"
          group-class="gl-mb-0"
        />
        <input-field
          id="postal-code"
          v-model="form.zipCode"
          label="Postal code"
          test-id="postal-code"
          name="customer[zip_code]"
          :rules="$options.FIELD_VALIDATIONS.postalCode"
          group-class="gl-mb-0"
        />

        <country-select name="customer[country]" group-class="gl-mb-0" @change="countryChanged" />

        <state-select name="customer[state]" :country="selectedCountry" group-class="gl-mb-0" />

        <input-field
          v-if="taxFieldVisible"
          id="tax-id"
          v-model="form.taxId"
          group-class="gl-mb-0"
          label="Tax ID"
          test-id="tax-id"
          :optional="true"
          :rules="$options.FIELD_VALIDATIONS.taxId"
          name="customer[vat_code]"
        >
          <template #label-help>
            <tax-id-help-icon-popover />
          </template>
        </input-field>
      </div>

      <gl-button class="gl-mt-6" type="submit" block variant="confirm">Continue</gl-button>
    </gl-form>
  </validation-observer>
</template>
